/**
 * @module app.cd
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, IForm } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { apiCall } from 'app/shared/lib/api/api';
import { MemberService } from 'app/cd/services/member-service';
import { Toaster } from 'app/shared/lib/toaster';
import { getReference } from 'app/shared/models/essette-types';
import { deepClone, forceNull } from 'app/shared/lib/utilities';
import * as Core from 'app/core/models/essette-api';
import * as CD from 'app/cd/models/essette-api';
import { validatePhoneNumber } from 'app/shared/lib/validation/validation-utilities';

@autoinject
export class SaveAlternatePhoneNumber extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    @bindable.objectNull({ changeHandler: 'reload' })
    protected phoneNumber: CD.MemberPhoneNumber | null = null;

    protected editedPhoneNumber: Core.PhoneNumber | null = null;

    @bindable({ defaultBindingMode: bindingMode.toView })
    public memberId: string | null = null;

    @bindable({ defaultBindingMode: bindingMode.toView })
    protected types: ReadonlyArray<Core.PhoneNumberType> = [];

    protected maxTextLength: number = 50;
    protected maxExtensionLength: number = 20;

    protected validatePhoneNumber = validatePhoneNumber;


    ////****************** Element References ***************************************////



    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly memberService: MemberService,
        private readonly toaster: Toaster,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        this.reload();
    }


    ////****************** Events ***************************************************////
    protected async reload(): Promise<void> {
        if(this.phoneNumber) {
            this.editedPhoneNumber = deepClone(this.phoneNumber);
        } else {
            this.editedPhoneNumber = null;
        }
    }


    ////****************** Functions ************************************************////
    private loadPhoneNumber() {
        if(this.phoneNumber) {
            this.editedPhoneNumber = deepClone(this.phoneNumber);
        } else {
            this.editedPhoneNumber = null;
        }
    }

    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }

    public reset(): void {
        if(this.phoneNumber) {
            this.editedPhoneNumber = deepClone(this.phoneNumber);
        }
    }

    public submittedChanges?: any;
    public async submit(): Promise<boolean> {
        if(await this.savePhoneNumber()) {
            this.submittedChanges = this.editedPhoneNumber;
            return true;
        }
        return false;
    }

    protected buildPhoneNumber(): CD.MemberPhoneNumber {
        const phoneNumber: CD.MemberPhoneNumber = {
            id: this.editedPhoneNumber?.id,
            type: getReference(this.editedPhoneNumber?.type),
            description: forceNull(this.editedPhoneNumber?.description),
            number: this.editedPhoneNumber?.number?.replace(/[^0-9]/g, ''), // regex to remove all non-digits
            extension: forceNull(this.editedPhoneNumber?.extension),
            member: { id: this.memberId }
        };

        return phoneNumber;
    }


    ////****************** Service Calls ********************************************////
    @apiCall
    private async savePhoneNumber(): Promise<boolean> {
        if (!this.editedPhoneNumber) return false;

        try {
            this.state.beginSave();

            const response = await this.memberService.saveMemberPhoneNumber(this.buildPhoneNumber());
            this.toaster.standard.saved('cd:memberContact.phoneNumber.alternate.$', response.isSuccessful);
            return response.isSuccessful;
        }
        catch(error) {
            this.toaster.standard.saved('cd:memberContact.phoneNumber.alternate.$', false);
            throw error;
        }
        finally {
            this.state.endSave();
        }
    }
}
